import { useState } from "react";
import { format, subDays } from "date-fns";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Construction, Sparkles } from "lucide-react";

import { CashFlowDetailsFilters, type CashFlowDetailsFiltersValue } from "@/components/finance/reports/cash-flow-details/CashFlowDetailsFilters";
import { OpeningBalancesTable } from "@/components/finance/reports/cash-flow-details/DataTable";
import { useBankAccounts } from "@/hooks/use-bank-accounts";

export default function CashFlowDetailsPage() {
    const today = format(new Date(), "yyyy-MM-dd");
    const thirtyDaysAgo = format(subDays(new Date(), 30), "yyyy-MM-dd");

    const [filters, setFilters] = useState<CashFlowDetailsFiltersValue>({
        bankAccountId: undefined, // теперь не обязателен
        dateFrom: thirtyDaysAgo,
        dateTo: today,
    });

    const { data: accounts = [], isLoading: accountsLoading } = useBankAccounts(true);
    const visibleAccounts = filters.bankAccountId
        ? accounts.filter(a => a.id === filters.bankAccountId)
        : accounts;

    return (
        <Layout>
            <div className="space-y-6 pb-12 animate-in fade-in duration-700">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-1 bg-primary rounded-full" />
                            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                                Cash Flow Details
                            </h1>
                        </div>
                        <p className="text-muted-foreground font-medium flex items-center gap-2">
                            <Badge
                                variant="secondary"
                                className="bg-slate-100 hover:bg-slate-200 text-slate-600 border-none px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider"
                            >
                                Deep Dive
                            </Badge>
                            Granular liquidity breakdown & drill-down analytics
                        </p>
                    </div>
                </div>

                {/* Filters (можешь оставить account select внутри, но он больше не нужен для таблицы “по всем”) */}
                <CashFlowDetailsFilters
                    value={filters}
                    onChange={setFilters}
                    activeOnly
                />

                {/* Table */}
                {!accountsLoading && visibleAccounts.length > 0 ? (
                    <OpeningBalancesTable
                        accounts={visibleAccounts}
                        dateFrom={filters.dateFrom}
                        dateTo={filters.dateTo}
                    />
                ) : (
                    <Card className="border-none shadow-lg overflow-hidden bg-white">
                        <CardContent className="p-6 text-sm text-slate-500">
                            {accountsLoading
                                ? "Loading accounts…"
                                : filters.bankAccountId
                                    ? "Selected account not found (or inactive)."
                                    : "No active bank accounts found."}
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}